import {
  EmptyState,
  LoadingState,
  ProblemBanner,
  SchemaForm,
  type SchemaFormValues,
  splitTicketValues,
  ticketToFormValues,
  toast,
} from '@servicetim/ui/components';
import { useMemo } from 'react';
import { useTicketFormSource, withoutFields } from './form-source';
import { useTicketContext } from './ticket-context';

/** Fields with their own UI on the detail screen (header, description, right rail). */
const HANDLED_ELSEWHERE = ['summary', 'description', 'assignee_id', 'labels', 'due_at'];

/**
 * Custom fields (plus priority) rendered with `SchemaForm` from the project's edit form or
 * bindings, honouring read-only/hidden, saved as one `PATCH` with `If-Match` (S-05-03).
 */
export function TicketFields() {
  const { ticket, canAdmin, patch, patching } = useTicketContext();
  const source = useTicketFormSource({
    projectId: ticket.projectId,
    issueTypeId: ticket.issueTypeId,
    kind: 'edit',
    canAdmin,
    systemFields: ['priority_id'],
  });
  const sections = useMemo(
    () => (source.sections ? withoutFields(source.sections, HANDLED_ELSEWHERE) : undefined),
    [source.sections],
  );

  const submit = async (values: SchemaFormValues) => {
    const { system, fields } = splitTicketValues(values);
    const saved = await patch({
      ...system,
      ...(Object.keys(fields).length > 0 ? { fields } : {}),
    });
    if (saved) toast.success('Fields saved');
  };

  return (
    <section aria-label="Fields" className="space-y-2">
      <h2 className="font-semibold text-fg text-sm">Fields</h2>
      {source.error ? <ProblemBanner error={source.error} size="sm" /> : null}
      {source.isPending ? <LoadingState rows={3} label="Loading fields…" /> : null}
      {sections && sections.length === 0 ? (
        <EmptyState
          size="sm"
          title="No fields to show"
          description="This project has no custom fields bound for this issue type."
        />
      ) : null}
      {sections && sections.length > 0 ? (
        <div className="rounded-md border border-border bg-surface-raised p-4">
          <SchemaForm
            key={`${ticket.id}:${ticket.version}:${source.source}`}
            sections={sections}
            defaultValues={ticketToFormValues(ticket)}
            display={ticket.display}
            onSubmit={submit}
            submitLabel="Save fields"
            busy={patching}
            disabled={!ticket.canEdit}
            hideActions={!ticket.canEdit}
            emptyValue="null"
          />
        </div>
      ) : null}
    </section>
  );
}
